import { Avatar, Badge, ProgressBar } from '@ntconsult/aluma-react';
import { TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ProfessionalWithAllocations } from '../types/Allocation';

type ProfessionalCapacityCellProps = {
  professional: ProfessionalWithAllocations;
  /** Pico de utilização na janela (pode passar de 100 = sobrealocado). */
  peak: number;
  /** 12 meses: a coluna fica fixa (`sticky left`) enquanto as barras rolam por baixo. */
  sticky: boolean;
};

/** Cor do medidor pelo pico de utilização: vazio=neutro, com folga=marca, cheio=sucesso, estourou=perigo. */
function meterTone(peak: number): 'neutral' | 'brand' | 'success' | 'danger' {
  if (peak > 100) return 'danger';
  if (peak >= 90) return 'success';
  if (peak === 0) return 'neutral';
  return 'brand';
}

/**
 * Coluna esquerda do Gantt: identidade do profissional + leitura de capacidade
 * na janela (pico de utilização e sobrealocação). Tudo em texto — teclado e
 * leitor de tela recebem o sinal sem depender de hover.
 */
export function ProfessionalCapacityCell({
  professional,
  peak,
  sticky,
}: ProfessionalCapacityCellProps) {
  const { t } = useTranslation('alocacao');
  const count = professional.allocations.length;

  return (
    <div
      className={`flex flex-col gap-1.5 border-border border-t px-3 py-2 transition-colors hover:bg-surface-hover ${
        sticky ? 'sticky left-0 z-20 bg-surface' : 'z-10'
      }`}
    >
      <div className="flex min-w-0 items-center gap-2">
        <Avatar name={professional.name} size="sm" tone="brand" />
        <div className="min-w-0">
          <p className="truncate text-body text-foreground" title={professional.name}>
            {professional.name}
          </p>
          {professional.registrationNumber !== null && (
            <p className="text-2xs text-foreground-subtle tabular-nums">
              {professional.registrationNumber}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <ProgressBar
          className="min-w-24 flex-1"
          value={peak}
          tone={meterTone(peak)}
          size="sm"
          label={t('linhaDoTempo.utilizacao', { valor: peak })}
        />
        {peak > 100 ? (
          <Badge variant="soft" tone="danger" size="sm" icon={<TriangleAlert size={12} />}>
            {t('linhaDoTempo.sobrealocadoValor', { valor: peak - 100 })}
          </Badge>
        ) : peak === 0 ? (
          <Badge variant="soft" tone="neutral" size="sm">
            {t('linhaDoTempo.disponivel')}
          </Badge>
        ) : (
          <span className="shrink-0 text-caption text-foreground-muted tabular-nums">{peak}%</span>
        )}
      </div>

      <span className="text-2xs text-foreground-muted">
        {t('linhaDoTempo.alocacoesContagem', { count })}
      </span>
    </div>
  );
}
